import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import IndexPage from 'flarum/forum/components/IndexPage';
import UserPage from 'flarum/forum/components/UserPage';
import LinkButton from 'flarum/common/components/LinkButton';
import Page from 'flarum/common/components/Page';
import { t } from '../common/text';
import Launcher from './components/Launcher';
import { act, celebrate, own, remember, showsLauncher, stale } from './state';
import { GraduatedNotification, ReminderNotification, StepCompletedNotification } from './notifications';
import { JourneyWidget, registerWidgets } from './widgets';
import { placed } from '../common/fofWidgets';
export { default as Ring } from './components/Ring';
app.initializers.add('ernestdefoe/onramp', () => {
  // 🚨 Both pages are their own chunks: nothing in them loads on other pages.
  app.routes.onramp = {
    path: '/onramp',
    component: () => import(/* webpackChunkName: 'forum/components/OnrampPage', webpackMode: 'lazy-once' */ './components/OnrampPage')
  };
  app.routes['user.onramp'] = {
    path: '/u/:username/onramp',
    component: () => import(/* webpackChunkName: 'forum/components/MemberOnrampPage', webpackMode: 'lazy-once' */ './components/MemberOnrampPage')
  };
  app.notificationComponents.onrampStepCompleted = StepCompletedNotification;
  app.notificationComponents.onrampGraduated = GraduatedNotification;
  app.notificationComponents.onrampReminder = ReminderNotification;
  extend('flarum/forum/components/NotificationGrid', 'notificationTypes', function (items) {
    if (!own()) return;
    items.add('onrampStepCompleted', {
      name: 'onrampStepCompleted',
      icon: 'fas fa-circle-check',
      label: t('forum.settings.notify_step')
    });
    items.add('onrampGraduated', {
      name: 'onrampGraduated',
      icon: 'fas fa-flag-checkered',
      label: t('forum.settings.notify_graduated')
    });
    items.add('onrampReminder', {
      name: 'onrampReminder',
      icon: 'fas fa-route',
      label: t('forum.settings.notify_reminder')
    });
  });
  extend(HeaderSecondary.prototype, 'items', function (items) {
    if (showsLauncher()) items.add('onramp', m(Launcher), 16);
  });

  // Where the forum lists its own pages, while the journey is open.
  extend(IndexSidebar.prototype, 'navItems', function (items) {
    if (!showsLauncher()) return;
    items.add('onramp', LinkButton.component({
      href: app.route('onramp'),
      icon: 'fas fa-route'
    }, t('forum.page.title')), 7);
  });

  // The stock sidebar gets the widget on the index unless FoF's layout places
  // it — only for members on a journey, so nobody else's sidebar changes at
  // all. 🚨 Placed, not installed: FoF installed with no Onramp widget placed
  // used to leave the member with no journey card anywhere.
  extend(IndexSidebar.prototype, 'items', function (items) {
    if (!showsLauncher() || placed(app, 'ernestdefoe-onramp', 'onrampJourney') || !app.current?.matches(IndexPage)) return;
    items.add('onrampJourney', m(JourneyWidget), -15);
  });
  extend(UserPage.prototype, 'navItems', function (items) {
    if (!this.user || !app.forum.attribute('canViewOnrampMembers')) return;
    items.add('onramp', LinkButton.component({
      href: app.route('user.onramp', {
        username: this.user.slug()
      }),
      icon: 'fas fa-route'
    }, t('forum.member.tab')), 70);
  });

  /*
   * 🚨 The news of a completed step rides in the `meta` of the very response
   * that completed it — the reply, the like, the avatar upload. Every request
   * goes through app.request, so this is the one place to hear it, whatever
   * the page and whichever extension made the request.
   */
  override(app, 'request', function (original, options) {
    return original(options).then(res => {
      // Only the forum's own API speaks for Onramp: a response from anywhere
      // else (an extension calling an outside service) is never read.
      const url = String(options?.url || '');
      const meta = url.startsWith(app.forum.attribute('apiUrl')) && res && typeof res === 'object' ? res.meta?.onramp : undefined;
      if (meta && Array.isArray(meta.completed)) {
        try {
          if (meta.state !== undefined) remember(meta.state ?? null);
          celebrate(meta, meta.state);
          m.redraw();
        } catch (e) {
          // The member's own request succeeded; a toast is never worth failing it.
        }
      }
      return res;
    });
  });

  /*
   * Visit steps: when the member lands on a page one of their pending steps
   * points at, tell the server. Nothing is sent on any other page, and
   * nothing at all once the journey is over.
   */
  let lastPath = '';
  // Every route draws a Page; after it is up, the route is the one to report.
  extend(Page.prototype, 'oncreate', () => setTimeout(() => reportVisit(), 0));
  function reportVisit() {
    const journey = own();
    if (!journey || journey.status !== 'open') return;
    const path = m.route.get() || '/';
    if (path === lastPath) return;
    lastPath = path;
    const wanted = journey.steps.some(s => s.type === 'visit' && !s.completedAt && s.path && samePage(s.path, path));
    if (wanted) act('visit', {
      path
    }).catch(() => {});
  }

  // J opens the launcher — unless the member is typing.
  document.addEventListener('keydown', e => {
    if (e.key !== 'j' && e.key !== 'J') return;
    if (e.metaKey || e.ctrlKey || e.altKey || !showsLauncher()) return;
    const el = e.target;
    if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
    const toggle = document.querySelector('.OnrampLauncher .Dropdown-toggle');
    if (toggle) {
      e.preventDefault();
      toggle.click();
    }
  });

  // After sibling extensions (Bespoke) have set up their registries.
  setTimeout(() => registerWidgets(), 0);

  // Anything the member did elsewhere (another tab) shows next time it opens.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') stale();
  });
});

/** The same page as the server decides it (see StepActionController::samePath). */
function samePage(want, have) {
  const norm = p => {
    let path = p;
    try {
      path = new URL(p, window.location.origin).pathname;
    } catch (e) {
      // A plain path is already what we want.
    }
    const base = new URL(app.forum.attribute('baseUrl')).pathname.replace(/\/$/, '');
    if (base && (path + '/').startsWith(base + '/')) path = path.slice(base.length);
    path = '/' + path.replace(/^\/+|\/+$/g, '');
    const d = path.match(/^\/d\/(\d+)/);
    return d ? `/d/${d[1]}` : path.toLowerCase();
  };
  return norm(want) === norm(have);
}flarum.reg.addChunkModule('ernestdefoe-onramp:forum/components/OnrampPage', '956', 'ernestdefoe-onramp', 'forum/components/OnrampPage');
flarum.reg.addChunkModule('ernestdefoe-onramp:forum/components/MemberOnrampPage', '286', 'ernestdefoe-onramp', 'forum/components/MemberOnrampPage');
flarum.reg.addChunkModule('ernestdefoe-onramp:forum/components/OnrampPage', '956', 'ernestdefoe-onramp', 'forum/actions');
flarum.reg.addChunkModule('ernestdefoe-onramp:forum/components/OnrampPage', '956', 'ernestdefoe-onramp', 'forum/components/StepRow');
flarum.reg.addChunkModule('ernestdefoe-onramp:forum/components/OnrampPage', '956', 'ernestdefoe-onramp', 'forum/styles');
flarum.reg.addChunkModule('ernestdefoe-onramp:forum/components/OnrampPage', '956', 'ernestdefoe-onramp', 'forum/generated/pagesCss');